/**
 * AppEditor — a comprehensive, single-scroll data-collection form over the full
 * AppConfig. Each product area is a <Card>. Saving PUTs the config, which creates
 * a new immutable app_version server-side (§3.2).
 *
 * Assets (icon, splash, onboarding slide images, no-internet icon) are uploaded
 * direct-to-R2 via the shared `uploadAsset` helper (assetsApi.upload) and previewed
 * inline via assetsApi.assetUrl. The server remains the authority on validation;
 * this form just collects a complete, well-typed object.
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  AppConfig,
  NavItem,
  OnboardingSlide,
  PageConfig,
  PageKind,
} from '@appbuilder/shared';
import { appsApi, assetsApi, ApiClientError } from '../api/client';
import { NAV_ICONS } from '../lib/navIcons';
import {
  Banner,
  Button,
  Card,
  Field,
  SelectField,
  TextAreaField,
  Toggle,
} from '../components/ui';

const DARK_MODE_OPTIONS = [
  { value: 'system', label: 'Follow system' },
  { value: 'light', label: 'Always light' },
  { value: 'dark', label: 'Always dark' },
  { value: 'userToggle', label: 'User toggle' },
] as const;

const SPLASH_PRESET_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'centered-logo', label: 'Centered logo' },
  { value: 'gradient', label: 'Gradient' },
] as const;

const LOADING_INDICATOR_OPTIONS = [
  { value: 'spinner', label: 'Spinner' },
  { value: 'bar', label: 'Progress bar' },
  { value: 'logo', label: 'Logo' },
  { value: 'none', label: 'None' },
] as const;

const ORIENTATION_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'portrait', label: 'Portrait' },
  { value: 'landscape', label: 'Landscape' },
] as const;

const NAV_MODEL_OPTIONS = [
  { value: 'bottomTabs', label: 'Bottom tabs' },
  { value: 'drawer', label: 'Drawer' },
  { value: 'topTabs', label: 'Top tabs' },
  { value: 'single', label: 'Single screen' },
] as const;

const PRESENTATION_OPTIONS = [
  { value: 'inApp', label: 'In-app WebView' },
  { value: 'chromeCustomTab', label: 'Chrome Custom Tab' },
  { value: 'systemBrowser', label: 'System browser' },
] as const;

const PAGE_KIND_OPTIONS: readonly { value: PageKind; label: string }[] = [
  { value: 'remote', label: 'Remote (website URL)' },
  { value: 'native', label: 'Native (SDUI)' },
  { value: 'custom_html', label: 'Custom HTML' },
];

const PUSH_PROVIDER_OPTIONS = [
  { value: 'onesignal', label: 'OneSignal' },
  { value: 'fcm', label: 'Firebase Cloud Messaging' },
] as const;

const CACHE_STRATEGY_OPTIONS = [
  { value: 'networkFirst', label: 'Network first' },
  { value: 'cacheFirst', label: 'Cache first' },
] as const;

const PERMISSION_KEY_OPTIONS = [
  { value: 'notifications', label: 'Notifications' },
  { value: 'camera', label: 'Camera' },
  { value: 'photoPicker', label: 'Photo picker' },
  { value: 'location', label: 'Location' },
  { value: 'microphone', label: 'Microphone' },
  { value: 'contacts', label: 'Contacts' },
] as const;

const HEADER_ALIGNMENT_OPTIONS = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
] as const;

const MAX_ONBOARDING_SLIDES = 4;
const MAX_NAV_ITEMS = 5;
const MAX_DRAWER_ITEMS = 8;
const MAX_HEADER_ITEMS = 4;

export function AppEditorPage(): JSX.Element {
  const { appId = '' } = useParams();
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    appsApi
      .getConfig(appId)
      .then((c) => !cancelled && setConfig(c))
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiClientError ? err.message : 'Failed to load config');
      });
    return () => {
      cancelled = true;
    };
  }, [appId]);

  // Immutable-update helper that produces a new config object.
  const patch = useCallback((updater: (c: AppConfig) => AppConfig) => {
    setConfig((prev) => (prev ? updater(prev) : prev));
  }, []);

  const onSave = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!config) return;
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const result = await appsApi.putConfig(appId, config);
      setNotice(`Saved as config version ${result.configVersion} (schema v${result.schemaVersion}).`);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(
          err.code === 'validation_error'
            ? `Validation failed: ${err.message}`
            : err.message,
        );
      } else {
        setError('Failed to save config');
      }
    } finally {
      setBusy(false);
    }
  };

  if (error && !config) {
    return (
      <div className="page">
        <Banner>{error}</Banner>
        <Link to="/apps">← Back to apps</Link>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="page">
        <p className="muted">Loading config…</p>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <Link to="/apps" className="muted">
            ← Apps
          </Link>
          <h1>{config.identity.name}</h1>
        </div>
        <Link to={`/apps/${appId}/build`} className="btn btn-secondary">
          Build →
        </Link>
      </div>

      {error && <Banner>{error}</Banner>}
      {notice && <Banner tone="success">{notice}</Banner>}

      <form onSubmit={onSave} className="editor">
        <WebsiteSection config={config} patch={patch} />
        <IdentitySection config={config} patch={patch} />
        <ThemeSection appId={appId} config={config} patch={patch} setError={setError} />
        <BehaviorSection config={config} patch={patch} />
        <OnboardingSection appId={appId} config={config} patch={patch} setError={setError} />
        <PagesSection config={config} patch={patch} />
        <NavigationSection config={config} patch={patch} />
        <DrawerSection config={config} patch={patch} />
        <HeaderSection appId={appId} config={config} patch={patch} setError={setError} />
        <PushSection config={config} patch={patch} />
        <OfflineSection config={config} patch={patch} />
        <NoInternetSection appId={appId} config={config} patch={patch} setError={setError} />
        <WhatsAppSection config={config} patch={patch} />
        <PermissionsSection config={config} patch={patch} />

        <div className="editor-save">
          <Button type="submit" loading={busy}>
            Save (creates new version)
          </Button>
          <span className="muted">
            Saving creates a new immutable version; you can build it next.
          </span>
        </div>
      </form>
    </div>
  );
}

type PatchFn = (updater: (c: AppConfig) => AppConfig) => void;

// ─── Shared asset upload + preview ─────────────────────────────────────────────

/**
 * Generalized direct-to-R2 upload used by every image field in the editor.
 * `apply` writes the returned assetId into the right place in the config.
 */
function useAssetUpload(
  appId: string,
  patch: PatchFn,
  setError: (s: string | null) => void,
): {
  uploadingKey: string | null;
  uploadAsset: (
    file: File,
    key: string,
    type: string,
    apply: (c: AppConfig, assetId: string) => AppConfig,
  ) => Promise<void>;
} {
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);

  const uploadAsset = async (
    file: File,
    key: string,
    type: string,
    apply: (c: AppConfig, assetId: string) => AppConfig,
  ): Promise<void> => {
    setError(null);
    setUploadingKey(key);
    try {
      const { assetId } = await assetsApi.upload(appId, file, type);
      patch((c) => apply(c, assetId));
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Asset upload failed');
    } finally {
      setUploadingKey(null);
    }
  };

  return { uploadingKey, uploadAsset };
}

function AssetUploadField({
  label,
  assetId,
  uploading,
  onFile,
}: {
  label: string;
  assetId?: string;
  uploading: boolean;
  onFile: (file: File) => void;
}): JSX.Element {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      <input
        type="file"
        accept="image/*"
        disabled={uploading}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
        }}
      />
      {assetId && (
        <img className="asset-preview" src={assetsApi.assetUrl(assetId)} alt={`${label} preview`} />
      )}
      <span className="field-hint">
        {uploading ? 'Uploading…' : assetId ? `Asset: ${assetId}` : 'Nothing uploaded yet'}
      </span>
    </div>
  );
}

// ─── 1. Website ────────────────────────────────────────────────────────────────

function WebsiteSection({ config, patch }: { config: AppConfig; patch: PatchFn }): JSX.Element {
  return (
    <Card
      title="Website"
      description="The website your app opens to — set the start URL it loads first."
    >
      <Field
        label="Start URL"
        type="url"
        className="mono"
        value={config.identity.startUrl ?? ''}
        onChange={(e) =>
          patch((c) => ({ ...c, identity: { ...c.identity, startUrl: e.target.value } }))
        }
        placeholder="https://your-site.com"
        hint="The primary website this app wraps."
      />
    </Card>
  );
}

// ─── 2. Identity ─────────────────────────────────────────────────────────────

function IdentitySection({ config, patch }: { config: AppConfig; patch: PatchFn }): JSX.Element {
  const id = config.identity;
  return (
    <Card
      title="Identity"
      description="Your app's name, Android package ID (its unique address on Google Play), and version."
    >
      <div className="form-row">
        <Field
          label="App name"
          value={id.name}
          onChange={(e) =>
            patch((c) => ({ ...c, identity: { ...c.identity, name: e.target.value } }))
          }
          required
        />
        <Field
          label="Android package"
          className="mono"
          value={id.androidPackage}
          onChange={(e) =>
            patch((c) => ({
              ...c,
              identity: { ...c.identity, androidPackage: e.target.value },
            }))
          }
          required
        />
      </div>
      <div className="form-row">
        <Field
          label="Version name"
          value={id.versionName}
          onChange={(e) =>
            patch((c) => ({ ...c, identity: { ...c.identity, versionName: e.target.value } }))
          }
          placeholder="1.0.0"
          required
        />
        <Field
          label="Version code"
          type="number"
          min={1}
          value={id.versionCode}
          onChange={(e) =>
            patch((c) => ({
              ...c,
              identity: { ...c.identity, versionCode: Number(e.target.value) || 1 },
            }))
          }
          hint="Must strictly increase per app (enforced server-side)."
          required
        />
      </div>
    </Card>
  );
}

// ─── 3. Theme & branding (+ asset upload to R2) ───────────────────────────────

function ThemeSection({
  appId,
  config,
  patch,
  setError,
}: {
  appId: string;
  config: AppConfig;
  patch: PatchFn;
  setError: (s: string | null) => void;
}): JSX.Element {
  const theme = config.theme;
  const { uploadingKey, uploadAsset } = useAssetUpload(appId, patch, setError);

  const setColor = (key: string, value: string): void => {
    patch((c) => ({
      ...c,
      theme: { ...c.theme, colors: { ...c.theme.colors, [key]: value } },
    }));
  };

  return (
    <Card
      title="Theme & branding"
      description="Colors, dark mode, app icon, and splash — how your app looks."
    >
      <SelectField
        label="Dark mode"
        value={theme.darkMode}
        onChange={(e) =>
          patch((c) => ({
            ...c,
            theme: { ...c.theme, darkMode: e.target.value as AppConfig['theme']['darkMode'] },
          }))
        }
        options={DARK_MODE_OPTIONS}
      />

      <div className="form-row">
        <Field
          label="Primary color"
          type="color"
          value={theme.colors.primary ?? '#3b5bdb'}
          onChange={(e) => setColor('primary', e.target.value)}
        />
        <Field
          label="Background color"
          type="color"
          value={theme.colors.background ?? '#ffffff'}
          onChange={(e) => setColor('background', e.target.value)}
        />
        <Field
          label="Accent color"
          type="color"
          value={theme.colors.accent ?? '#4263eb'}
          onChange={(e) => setColor('accent', e.target.value)}
        />
        <Field
          label="Text color"
          type="color"
          value={theme.colors.text ?? '#11181c'}
          onChange={(e) => setColor('text', e.target.value)}
        />
      </div>

      <div className="form-row">
        <SelectField
          label="Splash preset"
          value={theme.splashPreset ?? 'centered-logo'}
          onChange={(e) =>
            patch((c) => ({ ...c, theme: { ...c.theme, splashPreset: e.target.value } }))
          }
          options={SPLASH_PRESET_OPTIONS}
        />
        <SelectField
          label="Loading indicator"
          value={theme.loadingIndicator ?? 'spinner'}
          onChange={(e) =>
            patch((c) => ({
              ...c,
              theme: {
                ...c.theme,
                loadingIndicator: e.target.value as NonNullable<
                  AppConfig['theme']['loadingIndicator']
                >,
              },
            }))
          }
          options={LOADING_INDICATOR_OPTIONS}
        />
      </div>

      <div className="form-row">
        <AssetUploadField
          label="App icon"
          assetId={theme.appIconAssetId}
          uploading={uploadingKey === 'icon'}
          onFile={(f) =>
            void uploadAsset(f, 'icon', 'icon', (c, assetId) => ({
              ...c,
              theme: { ...c.theme, appIconAssetId: assetId },
            }))
          }
        />
        <AssetUploadField
          label="Splash image"
          assetId={theme.splashAssetId}
          uploading={uploadingKey === 'splash'}
          onFile={(f) =>
            void uploadAsset(f, 'splash', 'splash', (c, assetId) => ({
              ...c,
              theme: { ...c.theme, splashAssetId: assetId },
            }))
          }
        />
      </div>
    </Card>
  );
}

// ─── 4. Behavior ───────────────────────────────────────────────────────────────

function BehaviorSection({ config, patch }: { config: AppConfig; patch: PatchFn }): JSX.Element {
  const behavior = config.behavior ?? {};
  const setBehavior = (next: Partial<NonNullable<AppConfig['behavior']>>): void => {
    patch((c) => ({ ...c, behavior: { ...c.behavior, ...next } }));
  };

  return (
    <Card
      title="Behavior"
      description="Shell behaviors: edge-to-edge display, pull-to-refresh, orientation, keep-awake."
    >
      <div className="form-row">
        <Toggle
          label="Edge-to-edge"
          checked={behavior.edgeToEdge ?? false}
          onChange={(v) => setBehavior({ edgeToEdge: v })}
        />
        <Toggle
          label="Pull to refresh"
          checked={behavior.pullToRefresh ?? false}
          onChange={(v) => setBehavior({ pullToRefresh: v })}
        />
        <Toggle
          label="Desktop mode"
          checked={behavior.desktopMode ?? false}
          onChange={(v) => setBehavior({ desktopMode: v })}
        />
        <Toggle
          label="Keep awake"
          checked={behavior.keepAwake ?? false}
          onChange={(v) => setBehavior({ keepAwake: v })}
        />
      </div>
      <SelectField
        label="Orientation"
        value={behavior.orientation ?? 'auto'}
        onChange={(e) =>
          setBehavior({
            orientation: e.target.value as NonNullable<AppConfig['behavior']>['orientation'],
          })
        }
        options={ORIENTATION_OPTIONS}
      />
    </Card>
  );
}

// ─── 5. Onboarding ─────────────────────────────────────────────────────────────

function OnboardingSection({
  appId,
  config,
  patch,
  setError,
}: {
  appId: string;
  config: AppConfig;
  patch: PatchFn;
  setError: (s: string | null) => void;
}): JSX.Element {
  const onboarding = config.onboarding ?? { enabled: false, slides: [] };
  const { uploadingKey, uploadAsset } = useAssetUpload(appId, patch, setError);

  const updateSlide = (idx: number, next: Partial<OnboardingSlide>): void => {
    patch((c) => {
      const ob = c.onboarding ?? { enabled: false, slides: [] };
      return {
        ...c,
        onboarding: {
          ...ob,
          slides: ob.slides.map((s, i) => (i === idx ? { ...s, ...next } : s)),
        },
      };
    });
  };

  const addSlide = (): void => {
    patch((c) => {
      const ob = c.onboarding ?? { enabled: false, slides: [] };
      if (ob.slides.length >= MAX_ONBOARDING_SLIDES) return c;
      const slide: OnboardingSlide = {
        id: `slide-${ob.slides.length + 1}`,
        title: '',
        body: '',
      };
      return { ...c, onboarding: { ...ob, slides: [...ob.slides, slide] } };
    });
  };

  const removeSlide = (idx: number): void => {
    patch((c) => {
      const ob = c.onboarding ?? { enabled: false, slides: [] };
      return { ...c, onboarding: { ...ob, slides: ob.slides.filter((_, i) => i !== idx) } };
    });
  };

  return (
    <Card
      title="Onboarding"
      description="Optional welcome slides shown the first time someone opens the app."
      actions={
        <Button
          type="button"
          variant="secondary"
          className="btn-sm"
          onClick={addSlide}
          disabled={onboarding.slides.length >= MAX_ONBOARDING_SLIDES}
        >
          Add slide
        </Button>
      }
    >
      <Toggle
        label="Enable onboarding"
        checked={onboarding.enabled ?? false}
        onChange={(v) =>
          patch((c) => {
            const ob = c.onboarding ?? { enabled: false, slides: [] };
            return { ...c, onboarding: { ...ob, enabled: v } };
          })
        }
      />

      {onboarding.slides.map((slide, idx) => (
        <div key={slide.id} className="page-item">
          <AssetUploadField
            label={`Slide ${idx + 1} image`}
            assetId={slide.imageAssetId}
            uploading={uploadingKey === `onboarding-${idx}`}
            onFile={(f) =>
              void uploadAsset(f, `onboarding-${idx}`, 'onboarding', (c, assetId) => {
                const ob = c.onboarding ?? { enabled: false, slides: [] };
                return {
                  ...c,
                  onboarding: {
                    ...ob,
                    slides: ob.slides.map((s, i) =>
                      i === idx ? { ...s, imageAssetId: assetId } : s,
                    ),
                  },
                };
              })
            }
          />
          <Field
            label="Title"
            value={slide.title}
            onChange={(e) => updateSlide(idx, { title: e.target.value })}
          />
          <TextAreaField
            label="Body"
            rows={2}
            value={slide.body}
            onChange={(e) => updateSlide(idx, { body: e.target.value })}
          />
          <Field
            label="CTA label (optional)"
            value={slide.cta?.label ?? ''}
            onChange={(e) =>
              updateSlide(idx, {
                cta: e.target.value
                  ? {
                      label: e.target.value,
                      action: idx === onboarding.slides.length - 1 ? 'finish' : 'next',
                    }
                  : undefined,
              })
            }
          />
          <Button
            type="button"
            variant="danger"
            className="btn-sm"
            onClick={() => removeSlide(idx)}
          >
            Remove slide
          </Button>
        </div>
      ))}
    </Card>
  );
}

// ─── 6. Navigation & tabs ──────────────────────────────────────────────────────

/** Visual icon picker — tap an icon instead of typing a name. */
function IconPicker({ value, onChange }: { value?: string; onChange: (name: string) => void }): JSX.Element {
  return (
    <div className="icon-picker-field">
      <span className="icon-picker-title">Icon</span>
      <div className="icon-picker">
        {NAV_ICONS.map((opt) => (
          <button
            type="button"
            key={opt.name}
            className={`icon-opt${value === opt.name ? ' selected' : ''}`}
            onClick={() => onChange(opt.name)}
            title={opt.label}
          >
            <span className="icon-opt-emoji" aria-hidden>{opt.emoji}</span>
            <span className="icon-opt-label">{opt.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function NavigationSection({ config, patch }: { config: AppConfig; patch: PatchFn }): JSX.Element {
  const updateItem = (idx: number, next: Partial<NavItem>): void => {
    patch((c) => ({
      ...c,
      navigation: {
        ...c.navigation,
        items: c.navigation.items.map((it, i) => (i === idx ? { ...it, ...next } : it)),
      },
    }));
  };

  const addItem = (): void => {
    patch((c) => {
      if (c.navigation.items.length >= MAX_NAV_ITEMS) return c;
      const firstPageId = c.pages[0]?.id ?? 'home';
      const newItem: NavItem = {
        id: `nav-${c.navigation.items.length + 1}`,
        label: 'New tab',
        target: { pageId: firstPageId },
      };
      return { ...c, navigation: { ...c.navigation, items: [...c.navigation.items, newItem] } };
    });
  };

  const removeItem = (idx: number): void => {
    patch((c) => ({
      ...c,
      navigation: { ...c.navigation, items: c.navigation.items.filter((_, i) => i !== idx) },
    }));
  };

  const pageOptions = useMemo(
    () => config.pages.map((p) => ({ value: p.id, label: `${p.title} (${p.id})` })),
    [config.pages],
  );

  const firstPageId = config.pages[0]?.id ?? 'home';

  return (
    <Card
      title="Navigation & tabs"
      description="The main navigation (e.g. bottom tabs). Each item opens a page or a link."
      actions={
        <Button
          type="button"
          variant="secondary"
          className="btn-sm"
          onClick={addItem}
          disabled={config.navigation.items.length >= MAX_NAV_ITEMS}
        >
          Add item
        </Button>
      }
    >
      <SelectField
        label="Navigation model"
        value={config.navigation.model}
        onChange={(e) =>
          patch((c) => ({
            ...c,
            navigation: {
              ...c.navigation,
              model: e.target.value as AppConfig['navigation']['model'],
            },
          }))
        }
        options={NAV_MODEL_OPTIONS}
      />

      <p className="field-hint">
        Each tab opens one of your pages. To link to an external site, add a <strong>Website</strong> page
        in the Pages section, then point a tab at it.
      </p>

      {config.navigation.items.map((item, idx) => (
        <div key={item.id} className="page-item">
          <Field
            label="Label"
            value={item.label}
            onChange={(e) => updateItem(idx, { label: e.target.value })}
          />
          <IconPicker value={item.icon} onChange={(name) => updateItem(idx, { icon: name })} />
          <SelectField
            label="Opens page"
            value={'pageId' in item.target ? item.target.pageId : ''}
            onChange={(e) => updateItem(idx, { target: { pageId: e.target.value } })}
            options={pageOptions}
          />
          <Button
            type="button"
            variant="danger"
            className="btn-sm"
            onClick={() => removeItem(idx)}
          >
            Remove tab
          </Button>
        </div>
      ))}
    </Card>
  );
}

// ─── 6b. Shared NavItem row editor (drawer + header) ─────────────────────────

/** Next id for an item list that won't collide after a middle item is removed
 *  (scans the max numeric suffix rather than using the current length). */
function nextItemId(prefix: string, items: readonly NavItem[]): string {
  const max = items.reduce((m, it) => {
    const n = it.id.startsWith(`${prefix}-`) ? Number(it.id.slice(prefix.length + 1)) : NaN;
    return Number.isFinite(n) && n > m ? n : m;
  }, 0);
  return `${prefix}-${max + 1}`;
}

function NavItemFields({
  item,
  pageOptions,
  firstPageId,
  withIcon,
  onChange,
  onRemove,
}: {
  item: NavItem;
  pageOptions: readonly { value: string; label: string }[];
  firstPageId: string;
  withIcon?: boolean;
  onChange: (next: Partial<NavItem>) => void;
  onRemove: () => void;
}): JSX.Element {
  const isUrlTarget = 'url' in item.target;
  return (
    <div className="page-item">
      <div className="form-row">
        <Field
          label="Label"
          value={item.label}
          onChange={(e) => onChange({ label: e.target.value })}
        />
        {withIcon && (
          <Field
            label="Icon"
            value={item.icon ?? ''}
            onChange={(e) => onChange({ icon: e.target.value })}
            placeholder="e.g. search"
          />
        )}
      </div>

      <Toggle
        label="Link to external URL (instead of a page)"
        checked={isUrlTarget}
        onChange={(toUrl) => onChange({ target: toUrl ? { url: '' } : { pageId: firstPageId } })}
      />

      {isUrlTarget ? (
        <Field
          label="External URL"
          type="url"
          className="mono"
          value={'url' in item.target ? item.target.url : ''}
          onChange={(e) => onChange({ target: { url: e.target.value } })}
          placeholder="https://your-site.com/section"
        />
      ) : (
        <SelectField
          label="Target page"
          value={'pageId' in item.target ? item.target.pageId : ''}
          onChange={(e) => onChange({ target: { pageId: e.target.value } })}
          options={pageOptions}
        />
      )}

      <div className="form-row">
        <Toggle
          label="Open externally"
          checked={item.openExternally ?? false}
          onChange={(v) => onChange({ openExternally: v })}
        />
        <SelectField
          label="Presentation"
          value={item.presentation ?? 'inApp'}
          onChange={(e) =>
            onChange({ presentation: e.target.value as NonNullable<NavItem['presentation']> })
          }
          options={PRESENTATION_OPTIONS}
        />
      </div>

      <Button type="button" variant="danger" className="btn-sm" onClick={onRemove}>
        Remove item
      </Button>
    </div>
  );
}

// ─── 6c. Side drawer ─────────────────────────────────────────────────────────

function DrawerSection({ config, patch }: { config: AppConfig; patch: PatchFn }): JSX.Element {
  const drawer = config.drawer ?? { enabled: false, items: [] };
  const items = drawer.items ?? [];
  const firstPageId = config.pages[0]?.id ?? 'home';
  const pageOptions = useMemo(
    () => config.pages.map((p) => ({ value: p.id, label: `${p.title} (${p.id})` })),
    [config.pages],
  );

  const setEnabled = (enabled: boolean): void =>
    patch((c) => ({ ...c, drawer: { ...(c.drawer ?? { items: [] }), enabled } }));

  const updateItem = (idx: number, next: Partial<NavItem>): void =>
    patch((c) => {
      const d = c.drawer ?? { enabled: false, items: [] };
      const list = d.items ?? [];
      return {
        ...c,
        drawer: { ...d, items: list.map((it, i) => (i === idx ? { ...it, ...next } : it)) },
      };
    });

  const addItem = (): void =>
    patch((c) => {
      const d = c.drawer ?? { enabled: false, items: [] };
      const list = d.items ?? [];
      if (list.length >= MAX_DRAWER_ITEMS) return c;
      const item: NavItem = {
        id: nextItemId('drawer', list),
        label: 'New item',
        target: { pageId: c.pages[0]?.id ?? 'home' },
      };
      return { ...c, drawer: { ...d, items: [...list, item] } };
    });

  const removeItem = (idx: number): void =>
    patch((c) => {
      const d = c.drawer ?? { enabled: false, items: [] };
      const list = d.items ?? [];
      return { ...c, drawer: { ...d, items: list.filter((_, i) => i !== idx) } };
    });

  return (
    <Card
      title="Side drawer"
      description="A slide-in side menu (☰) with links to your pages or external sites."
      actions={
        <Button
          type="button"
          variant="secondary"
          className="btn-sm"
          onClick={addItem}
          disabled={!drawer.enabled || items.length >= MAX_DRAWER_ITEMS}
        >
          Add item
        </Button>
      }
    >
      <Toggle
        label="Enable side drawer (slide-in menu)"
        checked={drawer.enabled ?? false}
        onChange={setEnabled}
      />
      {drawer.enabled ? (
        items.length === 0 ? (
          <p className="field-hint">No drawer items yet — add links to your pages or external URLs.</p>
        ) : (
          items.map((item, idx) => (
            <NavItemFields
              key={item.id}
              item={item}
              pageOptions={pageOptions}
              firstPageId={firstPageId}
              onChange={(next) => updateItem(idx, next)}
              onRemove={() => removeItem(idx)}
            />
          ))
        )
      ) : (
        <p className="field-hint">
          Turn this on to add a slide-in side menu with links to your pages or external sites.
        </p>
      )}
    </Card>
  );
}

// ─── 6d. Header (top bar) ────────────────────────────────────────────────────

function HeaderSection({
  appId,
  config,
  patch,
  setError,
}: {
  appId: string;
  config: AppConfig;
  patch: PatchFn;
  setError: (s: string | null) => void;
}): JSX.Element {
  const header = config.header ?? { enabled: false, items: [] };
  const items = header.items ?? [];
  const firstPageId = config.pages[0]?.id ?? 'home';
  const pageOptions = useMemo(
    () => config.pages.map((p) => ({ value: p.id, label: `${p.title} (${p.id})` })),
    [config.pages],
  );
  const { uploadingKey, uploadAsset } = useAssetUpload(appId, patch, setError);

  const setHeader = (next: Partial<NonNullable<AppConfig['header']>>): void =>
    patch((c) => ({ ...c, header: { ...(c.header ?? { items: [] }), ...next } }));

  const updateItem = (idx: number, next: Partial<NavItem>): void =>
    patch((c) => {
      const h = c.header ?? { enabled: false, items: [] };
      const list = h.items ?? [];
      return {
        ...c,
        header: { ...h, items: list.map((it, i) => (i === idx ? { ...it, ...next } : it)) },
      };
    });

  const addItem = (): void =>
    patch((c) => {
      const h = c.header ?? { enabled: false, items: [] };
      const list = h.items ?? [];
      if (list.length >= MAX_HEADER_ITEMS) return c;
      const item: NavItem = {
        id: nextItemId('header', list),
        label: 'Action',
        target: { pageId: c.pages[0]?.id ?? 'home' },
      };
      return { ...c, header: { ...h, items: [...list, item] } };
    });

  const removeItem = (idx: number): void =>
    patch((c) => {
      const h = c.header ?? { enabled: false, items: [] };
      const list = h.items ?? [];
      return { ...c, header: { ...h, items: list.filter((_, i) => i !== idx) } };
    });

  return (
    <Card
      title="Header (top bar)"
      description="The top app bar — title, logo, a menu button, and quick action links."
      actions={
        <Button
          type="button"
          variant="secondary"
          className="btn-sm"
          onClick={addItem}
          disabled={!header.enabled || items.length >= MAX_HEADER_ITEMS}
        >
          Add action
        </Button>
      }
    >
      <Toggle
        label="Enable header"
        checked={header.enabled ?? false}
        onChange={(v) => setHeader({ enabled: v })}
      />

      {header.enabled ? (
        <>
          <div className="form-row">
            <Field
              label="Title"
              value={header.title ?? ''}
              placeholder={config.identity.name || 'App name'}
              onChange={(e) => setHeader({ title: e.target.value })}
            />
            <SelectField
              label="Title alignment"
              value={header.alignment ?? 'left'}
              onChange={(e) =>
                setHeader({ alignment: e.target.value as NonNullable<AppConfig['header']>['alignment'] })
              }
              options={HEADER_ALIGNMENT_OPTIONS}
            />
          </div>

          <div className="form-row">
            <Toggle
              label="Show menu (☰) button — opens the drawer"
              checked={header.showMenuButton ?? false}
              onChange={(v) => setHeader({ showMenuButton: v })}
            />
            <Toggle
              label="Show logo"
              checked={header.showLogo ?? false}
              onChange={(v) => setHeader({ showLogo: v })}
            />
          </div>

          {header.showLogo && (
            <AssetUploadField
              label="Header logo"
              assetId={header.logoAssetId}
              uploading={uploadingKey === 'headerLogo'}
              onFile={(f) =>
                void uploadAsset(f, 'headerLogo', 'media', (c, assetId) => ({
                  ...c,
                  header: { ...(c.header ?? { enabled: true, items: [] }), logoAssetId: assetId },
                }))
              }
            />
          )}

          {items.length === 0 ? (
            <p className="field-hint">
              No header actions yet — add label/icon links to your pages or external URLs.
            </p>
          ) : (
            items.map((item, idx) => (
              <NavItemFields
                key={item.id}
                item={item}
                pageOptions={pageOptions}
                firstPageId={firstPageId}
                withIcon
                onChange={(next) => updateItem(idx, next)}
                onRemove={() => removeItem(idx)}
              />
            ))
          )}
        </>
      ) : (
        <p className="field-hint">
          Turn this on to show a top app bar with a title, optional logo, menu button, and action links.
        </p>
      )}
    </Card>
  );
}

// ─── 7. Pages ───────────────────────────────────────────────────────────────

function PagesSection({ config, patch }: { config: AppConfig; patch: PatchFn }): JSX.Element {
  const updatePage = (idx: number, next: Partial<PageConfig>): void => {
    patch((c) => ({
      ...c,
      pages: c.pages.map((p, i) => (i === idx ? { ...p, ...next } : p)),
    }));
  };

  const updateRemote = (idx: number, next: Partial<NonNullable<PageConfig['remote']>>): void => {
    patch((c) => ({
      ...c,
      pages: c.pages.map((p, i) =>
        i === idx
          ? { ...p, remote: { url: p.remote?.url ?? '', ...p.remote, ...next } }
          : p,
      ),
    }));
  };

  const addPage = (): void => {
    patch((c) => {
      const n = c.pages.length + 1;
      const page: PageConfig = {
        id: `page-${n}`,
        slug: `page-${n}`,
        title: `Page ${n}`,
        kind: 'remote',
        remote: { url: 'https://example.com', pullToRefresh: true },
      };
      return { ...c, pages: [...c.pages, page] };
    });
  };

  const removePage = (idx: number): void => {
    patch((c) => ({ ...c, pages: c.pages.filter((_, i) => i !== idx) }));
  };

  return (
    <Card
      title="Pages"
      description="The screens in your app. Each is a website URL, custom HTML, or a native screen."
      actions={
        <Button type="button" variant="secondary" className="btn-sm" onClick={addPage}>
          Add page
        </Button>
      }
    >
      {config.pages.map((page, idx) => (
        <div key={page.id} className="page-item">
          <div className="page-id-row">
            <span className="page-id-badge">ID&nbsp;<code>{page.id}</code></span>
            <span className="field-hint">
              Jump here from your site's HTML with <code>window.AppBuilder.navigate('{page.id}')</code>
            </span>
          </div>
          <div className="form-row">
            <Field
              label="Title"
              value={page.title}
              onChange={(e) => updatePage(idx, { title: e.target.value })}
            />
            <Field
              label="Slug"
              className="mono"
              value={page.slug}
              onChange={(e) => updatePage(idx, { slug: e.target.value })}
            />
            <SelectField
              label="Kind"
              value={page.kind}
              onChange={(e) => updatePage(idx, { kind: e.target.value as PageKind })}
              options={PAGE_KIND_OPTIONS}
            />
          </div>

          {page.kind === 'remote' && (
            <>
              <Field
                label="Remote URL"
                type="url"
                className="mono"
                value={page.remote?.url ?? ''}
                onChange={(e) => updateRemote(idx, { url: e.target.value })}
                placeholder="https://your-site.com"
              />
              <Toggle
                label="Pull to refresh"
                checked={page.remote?.pullToRefresh ?? false}
                onChange={(v) => updateRemote(idx, { pullToRefresh: v })}
              />
            </>
          )}

          {page.kind === 'custom_html' && (
            <>
              <TextAreaField
                label="Custom HTML"
                rows={4}
                value={page.customHtml?.html ?? ''}
                onChange={(e) =>
                  updatePage(idx, {
                    customHtml: {
                      html: e.target.value,
                      css: page.customHtml?.css,
                      jsEnabled: page.customHtml?.jsEnabled ?? false,
                    },
                  })
                }
                hint="Sanitized + sandboxed server-side at build time."
              />
              <TextAreaField
                label="Custom CSS"
                rows={3}
                value={page.customHtml?.css ?? ''}
                onChange={(e) =>
                  updatePage(idx, {
                    customHtml: {
                      html: page.customHtml?.html ?? '',
                      css: e.target.value,
                      jsEnabled: page.customHtml?.jsEnabled ?? false,
                    },
                  })
                }
              />
            </>
          )}

          {page.kind === 'native' && (
            <p className="muted">
              Native SDUI block editing is not part of the MVP form yet.
              {/* TODO(scaffold): native page block builder. */}
            </p>
          )}

          <Button
            type="button"
            variant="danger"
            className="btn-sm"
            onClick={() => removePage(idx)}
          >
            Remove page
          </Button>
        </div>
      ))}
    </Card>
  );
}

// ─── 8. Push notifications ─────────────────────────────────────────────────────

function PushSection({ config, patch }: { config: AppConfig; patch: PatchFn }): JSX.Element {
  const push = config.push ?? { enabled: false, provider: 'onesignal' };
  const setPush = (next: Partial<NonNullable<AppConfig['push']>>): void => {
    patch((c) => {
      const base = c.push ?? { enabled: false, provider: 'onesignal' };
      return { ...c, push: { ...base, ...next } };
    });
  };

  return (
    <Card
      title="Push notifications"
      description="Re-engage users with notifications (connect OneSignal or Firebase)."
    >
      <Toggle
        label="Enable push notifications"
        checked={push.enabled}
        onChange={(v) => setPush({ enabled: v })}
      />
      <div className="form-row">
        <SelectField
          label="Provider"
          value={push.provider}
          onChange={(e) =>
            setPush({ provider: e.target.value as NonNullable<AppConfig['push']>['provider'] })
          }
          options={PUSH_PROVIDER_OPTIONS}
        />
        <Toggle
          label="Prompt on launch"
          checked={push.promptOnLaunch ?? false}
          onChange={(v) => setPush({ promptOnLaunch: v })}
        />
      </div>
      <div className="form-row">
        <Field
          label="OneSignal App ID"
          className="mono"
          value={push.onesignalAppId ?? ''}
          onChange={(e) => setPush({ onesignalAppId: e.target.value })}
        />
        <Field
          label="OneSignal REST API Key"
          type="password"
          className="mono"
          value={push.onesignalRestApiKey ?? ''}
          onChange={(e) => setPush({ onesignalRestApiKey: e.target.value })}
          hint="Sensitive — stored server-side."
        />
      </div>
      <Field
        label="Topics"
        value={(push.topics ?? []).join(', ')}
        onChange={(e) =>
          setPush({
            topics: e.target.value
              .split(',')
              .map((t) => t.trim())
              .filter((t) => t.length > 0),
          })
        }
        hint="Comma-separated list of topic names."
      />
    </Card>
  );
}

// ─── 9. Offline ──────────────────────────────────────────────────────────────

function OfflineSection({ config, patch }: { config: AppConfig; patch: PatchFn }): JSX.Element {
  const offline = config.offline ?? { enabled: false };
  const setOffline = (next: Partial<NonNullable<AppConfig['offline']>>): void => {
    patch((c) => {
      const base = c.offline ?? { enabled: false };
      return { ...c, offline: { ...base, ...next } };
    });
  };

  const pageOptions = useMemo(
    () => [
      { value: '', label: '— none —' },
      ...config.pages.map((p) => ({ value: p.id, label: `${p.title} (${p.id})` })),
    ],
    [config.pages],
  );

  return (
    <Card
      title="Offline"
      description="Show cached content when the device has no connection."
    >
      <Toggle
        label="Enable offline caching"
        checked={offline.enabled}
        onChange={(v) => setOffline({ enabled: v })}
      />
      <div className="form-row">
        <SelectField
          label="Cache strategy"
          value={offline.cacheStrategy ?? 'networkFirst'}
          onChange={(e) =>
            setOffline({
              cacheStrategy: e.target.value as NonNullable<
                AppConfig['offline']
              >['cacheStrategy'],
            })
          }
          options={CACHE_STRATEGY_OPTIONS}
        />
        <SelectField
          label="Fallback page"
          value={offline.fallbackPageId ?? ''}
          onChange={(e) => setOffline({ fallbackPageId: e.target.value || undefined })}
          options={pageOptions}
        />
      </div>
    </Card>
  );
}

// ─── 10. No-internet screen ────────────────────────────────────────────────────

function NoInternetSection({
  appId,
  config,
  patch,
  setError,
}: {
  appId: string;
  config: AppConfig;
  patch: PatchFn;
  setError: (s: string | null) => void;
}): JSX.Element {
  const screen = config.noInternetScreen ?? {};
  const { uploadingKey, uploadAsset } = useAssetUpload(appId, patch, setError);

  const setScreen = (next: Partial<NonNullable<AppConfig['noInternetScreen']>>): void => {
    patch((c) => ({ ...c, noInternetScreen: { ...c.noInternetScreen, ...next } }));
  };

  return (
    <Card
      title="No-internet screen"
      description="A branded screen shown when the user is offline."
    >
      <AssetUploadField
        label="Icon"
        assetId={screen.iconAssetId}
        uploading={uploadingKey === 'no-internet-icon'}
        onFile={(f) =>
          void uploadAsset(f, 'no-internet-icon', 'media', (c, assetId) => ({
            ...c,
            noInternetScreen: { ...c.noInternetScreen, iconAssetId: assetId },
          }))
        }
      />
      <Field
        label="Title"
        value={screen.title ?? ''}
        onChange={(e) => setScreen({ title: e.target.value })}
      />
      <TextAreaField
        label="Description"
        rows={2}
        value={screen.description ?? ''}
        onChange={(e) => setScreen({ description: e.target.value })}
      />
      <Toggle
        label="Auto-retry when connection returns"
        checked={screen.autoRetry ?? false}
        onChange={(v) => setScreen({ autoRetry: v })}
      />
    </Card>
  );
}

// ─── 11. WhatsApp ──────────────────────────────────────────────────────────────

function WhatsAppSection({ config, patch }: { config: AppConfig; patch: PatchFn }): JSX.Element {
  const whatsapp = config.integrations?.whatsapp ?? { enabled: false };
  const setWhatsApp = (
    next: Partial<NonNullable<NonNullable<AppConfig['integrations']>['whatsapp']>>,
  ): void => {
    patch((c) => {
      const base = c.integrations?.whatsapp ?? { enabled: false };
      return {
        ...c,
        integrations: { ...c.integrations, whatsapp: { ...base, ...next } },
      };
    });
  };

  return (
    <Card
      title="WhatsApp"
      description="Add a one-tap WhatsApp chat button to your app."
    >
      <Toggle
        label="Enable WhatsApp button"
        checked={whatsapp.enabled}
        onChange={(v) => setWhatsApp({ enabled: v })}
      />
      <div className="form-row">
        <Field
          label="Phone number"
          className="mono"
          value={whatsapp.phone ?? ''}
          onChange={(e) => setWhatsApp({ phone: e.target.value })}
          placeholder="+15551234567"
        />
      </div>
      <TextAreaField
        label="Prefilled message"
        rows={2}
        value={whatsapp.message ?? ''}
        onChange={(e) => setWhatsApp({ message: e.target.value })}
        hint="Pre-populated into the chat when the user taps the button."
      />
    </Card>
  );
}

// ─── 12. Permissions ───────────────────────────────────────────────────────────

function PermissionsSection({ config, patch }: { config: AppConfig; patch: PatchFn }): JSX.Element {
  const permissions = config.permissions ?? [];

  const updatePermission = (
    idx: number,
    next: Partial<NonNullable<AppConfig['permissions']>[number]>,
  ): void => {
    patch((c) => ({
      ...c,
      permissions: (c.permissions ?? []).map((p, i) => (i === idx ? { ...p, ...next } : p)),
    }));
  };

  const addPermission = (): void => {
    patch((c) => ({
      ...c,
      permissions: [...(c.permissions ?? []), { key: 'notifications', rationale: '' }],
    }));
  };

  const removePermission = (idx: number): void => {
    patch((c) => ({
      ...c,
      permissions: (c.permissions ?? []).filter((_, i) => i !== idx),
    }));
  };

  return (
    <Card
      title="Permissions"
      description="Device permissions the app requests — keep these minimal; Google Play reviews them."
      actions={
        <Button type="button" variant="secondary" className="btn-sm" onClick={addPermission}>
          Add permission
        </Button>
      }
    >
      {permissions.length === 0 && <p className="muted">No permissions requested.</p>}
      {permissions.map((perm, idx) => (
        <div key={`${perm.key}-${idx}`} className="page-item">
          <div className="form-row">
            <SelectField
              label="Permission"
              value={perm.key}
              onChange={(e) => updatePermission(idx, { key: e.target.value })}
              options={PERMISSION_KEY_OPTIONS}
            />
            <Toggle
              label="Request lazily"
              checked={perm.requestLazily ?? false}
              onChange={(v) => updatePermission(idx, { requestLazily: v })}
            />
          </div>
          <TextAreaField
            label="Rationale"
            rows={2}
            value={perm.rationale}
            onChange={(e) => updatePermission(idx, { rationale: e.target.value })}
            hint="Shown to the user explaining why the app needs this."
          />
          <Button
            type="button"
            variant="danger"
            className="btn-sm"
            onClick={() => removePermission(idx)}
          >
            Remove permission
          </Button>
        </div>
      ))}
    </Card>
  );
}
